/**
 * EmptyState.jsx — Standardized empty-state pattern.
 *
 * Pattern: title + plain-English explanation + primary CTA + secondary CTA + optional example link.
 *
 * Use everywhere we have an empty list/page/section so users know what to do next.
 */
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export default function EmptyState({
  icon: Icon = null,
  title,
  description,
  primaryCta = null,
  secondaryCta = null,
  exampleLink = null,
  className,
  testId = 'empty-state',
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center py-10 px-6 bg-slate-50/50 dark:bg-slate-800/30 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl',
        className
      )}
      data-testid={testId}
    >
      {Icon && (
        <div className="w-12 h-12 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-slate-400" />
        </div>
      )}
      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1.5" data-testid={`${testId}-title`}>
        {title}
      </h3>
      {description && (
        <p
          className="text-xs text-slate-500 dark:text-slate-400 max-w-md leading-relaxed"
          data-testid={`${testId}-description`}
        >
          {description}
        </p>
      )}
      {(primaryCta || secondaryCta) && (
        <div className="flex items-center gap-2 mt-4 flex-wrap justify-center">
          {primaryCta && <CtaButton {...primaryCta} variant="default" testId={`${testId}-primary-cta`} />}
          {secondaryCta && (
            <CtaButton {...secondaryCta} variant="outline" testId={`${testId}-secondary-cta`} />
          )}
        </div>
      )}
      {exampleLink && (
        <a
          href={exampleLink.href}
          target={exampleLink.external ? '_blank' : undefined}
          rel={exampleLink.external ? 'noreferrer noopener' : undefined}
          className="text-[11px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline mt-3"
          data-testid={`${testId}-example-link`}
        >
          {exampleLink.label}
        </a>
      )}
    </div>
  );
}

function CtaButton({ label, to, onClick, variant, testId }) {
  if (to) {
    return (
      <Link to={to} data-testid={testId}>
        <Button variant={variant} size="sm" className="text-xs">
          {label}
        </Button>
      </Link>
    );
  }
  return (
    <Button variant={variant} size="sm" className="text-xs" onClick={onClick} data-testid={testId}>
      {label}
    </Button>
  );
}
